import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Lottie from "react-lottie-player";
import LoadingAnimationHands from "@/assets/animations/loadingAnimationHands.json";
import ModePreview from "@/widgets/modeOnboarding/components/ModePreview";
import { Check, X } from "lucide-react";
import useToastStore from "@/shared/zustand/toastStore";
import { useMainLayoutStore } from "@/shared/zustand/mainLayoutStore";
import { getModes } from "@/shared/utils/appLayoutUtils/appLayoutUtilities";
import { getModeById, updateMode } from "@/core/api/api";
const ModeOnboarding = ({ selectedModeId, handleCloseModeOnboarding, }) => {
    const showToast = useToastStore((state) => state.showToast);
    const [showPreviewDialog, setShowpreviewDialog] = useState(false);
    const [modePreviewData, setModePreviewData] = useState();
    const [loading, setLoading] = useState(false);
    const [modeData, setModeData] = useState(null);
    const [formEdited, setFormEdited] = useState(false);
    const { register, handleSubmit, reset, watch, formState: { errors, isDirty }, } = useForm({ mode: "onChange" });
    const watchedUrl = watch("url");
    const { setModeList } = useMainLayoutStore();
    // Fetch mode data when component mounts or selectedModeId changes
    useEffect(() => {
        const fetchModeData = async () => {
            if (!selectedModeId)
                return;
            try {
                setLoading(true);
                // old code for api call
                // const response = await axios.get(
                //   `${process.env.VITE_BASE_API_URL}/modes/${selectedModeId}`
                // );
                const { responseData, error } = await getModeById(selectedModeId);
                if (error) {
                    console.error("Error fetching mode data:", error);
                    showToast("error", "Error", error.errorMessage);
                    return;
                }
                if (!responseData) {
                    console.error("Error fetching mode data:", responseData);
                    showToast("error", "Error", "Failed to fetch mode data");
                    return;
                }
                setModeData(responseData);
                // Populate form with fetched data
                reset({
                    name: responseData.modeName,
                    url: responseData.url,
                    module: responseData.module?.replace("./", "") || "",
                    remote: responseData.remote,
                    modeDescription: responseData.modeDescription,
                });
                setFormEdited(false);
            }
            catch (error) {
                showToast("error", "Error", "Error while fetching the mode data, check console for more details");
                handleCloseModeOnboarding();
                console.error("Error fetching mode data:", error);
            }
            finally {
                setLoading(false);
            }
        };
        fetchModeData();
    }, [selectedModeId, reset]);
    // Track form changes
    useEffect(() => {
        setFormEdited(isDirty);
    }, [isDirty]);
    // Handle Preview
    const handlePreview = (data) => {
        setModePreviewData(data);
        setShowpreviewDialog(true);
    };
    // Handle Delete Mode (commented out for now && should be written in api.ts file with its format)
    // const handleDeleteMode = async () => {
    //   if (!selectedModeId) return;
    //   try {
    //     setLoading(true);
    //     await axios.delete(
    //       `${process.env.VITE_BASE_API_URL}/modes/${selectedModeId}`
    //     );
    //     console.log("Mode deleted successfully");
    //     handleCloseModeOnboarding();
    //   } catch (error) {
    //     showToast(
    //       "error",
    //       "Error",
    //       "Error while deleting the mode, check console for more details"
    //     );
    //     console.error("Error deleting mode:", error);
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    // Handle Form Submission (Update Mode)
    const onSubmit = async (data) => {
        if (!selectedModeId)
            return;
        try {
            setLoading(true);
            const payload = {
                modeName: data.name,
                url: data.url,
                modeDescription: data.modeDescription,
                module: `./${data.module}`,
                remote: data.remote,
                modeId: selectedModeId,
            };
            // old code for api call
            // await axios.put(
            //   `${process.env.VITE_BASE_API_URL}/modes/${selectedModeId}`,
            //   payload
            // );
            const { responseData, error } = await updateMode(selectedModeId, payload);
            if (error) {
                console.error("Error updating mode:", error);
                showToast("error", "Error", error.errorMessage);
                return;
            }
            console.log("Mode updated successfully");
            showToast("success", "Success", "Mode data updated successfully");
            // Update the local modeData state
            setModeData({
                modeId: selectedModeId,
                modeName: data.name,
                url: data.url,
                modeDescription: data.modeDescription,
                module: `./${data.module}`,
                remote: data.remote,
            });
            // Update the form's default values
            reset({
                name: data.name,
                url: data.url,
                module: data.module,
                remote: data.remote,
                modeDescription: data.modeDescription,
            }, { keepDirty: false }); // This ensures isDirty is reset
            const resultList = await getModes();
            if (resultList && resultList.length > 0)
                setModeList(resultList);
            setFormEdited(false);
            // handleCloseModeOnboarding();
        }
        catch (error) {
            showToast("error", "Error", "Error while updating the mode data, check console for more details");
            console.error("Error updating mode:", error);
        }
        finally {
            setLoading(false);
        }
    };
    return loading ? (_jsx("div", { className: "w-full h-full flex justify-center items-center", children: _jsx(Lottie, { animationData: LoadingAnimationHands, loop: true, play: true, className: "w-full md:w-1/2" }) })) : (_jsxs("div", { className: "w-full h-full flex flex-col space-y-4 font-content", children: [!showPreviewDialog && (_jsxs("div", { className: "flex flex-row justify-between items-center", children: [_jsx("h2", { className: "text-[#344054] font-heading text-2xl font-semibold", children: !showPreviewDialog
                            ? `${modeData?.modeName} Data`
                            : `${modePreviewData?.module} Preview` }), _jsx(Button, { icon: "pi pi-angle-left", label: "Go Back", text: true, className: "text-[#374151] ml-auto px-4 py-2 rounded-[6px] flex items-center gap-2 border border-[#D1D5DB]", onClick: handleCloseModeOnboarding })] })), !showPreviewDialog ? (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-y-4", children: [_jsxs("div", { children: [_jsxs("label", { htmlFor: "name", className: "block text-base font-normal", children: ["Mode Name ", _jsx("span", { className: "text-[red]", children: "*" })] }), _jsx("input", { type: "text", id: "name", ...register("name", { required: "Mode Name is required" }), className: `w-full mt-1 p-2 text-xs md:text-sm border rounded-[6px] ${errors.name ? "border-red-500" : "border-gray-300"}` }), errors.name && (_jsx("p", { className: "text-sm text-red-500", children: errors.name.message }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "url", className: "block text-base font-normal", children: "Remote URL" }), _jsx("input", { type: "url", id: "url", ...register("url", {
                                    pattern: {
                                        value: /^https?:\/\//,
                                        message: "Enter a valid URL",
                                    },
                                }), className: `w-full mt-1 p-2 text-xs md:text-sm border rounded-[6px] ${errors.url ? "border-red-500" : "border-gray-300"}` }), errors.url && (_jsx("p", { className: "text-sm text-red-500", children: errors.url.message }))] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "remote", className: "block text-base font-normal", children: ["Remote App's Name ", _jsx("span", { className: "text-[red]", children: "*" })] }), _jsx("input", { type: "text", id: "remote", ...register("remote", {
                                    required: "Remote App's name is required",
                                }), className: `w-full mt-1 p-2 text-xs md:text-sm border rounded-[6px] ${errors.remote ? "border-red-500" : "border-gray-300"}` }), errors.remote && (_jsx("p", { className: "text-sm text-red-500", children: errors.remote.message }))] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "module", className: "block text-base font-normal", children: ["Remote's Module to import ", _jsx("span", { className: "text-[red]", children: "*" })] }), _jsx("input", { type: "text", id: "module", ...register("module", { required: "Module is required" }), className: `w-full mt-1 p-2  text-xs md:text-sm border rounded-[6px] ${errors.module ? "border-red-500" : "border-gray-300"}` }), errors.module && (_jsx("p", { className: "text-sm text-red-500", children: errors.module.message }))] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "modeDescription", className: "block text-base font-normal", children: ["When To Apply Mode ", _jsx("span", { className: "text-[red]", children: "*" })] }), _jsx("textarea", { id: "modeDescription", rows: 3, ...register("modeDescription", {
                                    required: "Mode Description is required",
                                }), className: `w-full mt-1 p-2 text-xs md:text-sm border rounded-[6px] ${errors.modeDescription ? "border-red-500" : "border-gray-300"} resize-none` }), errors.modeDescription && (_jsx("p", { className: "text-sm text-red-500", children: errors.modeDescription.message }))] }), _jsxs("div", { className: "flex items-center justify-end gap-x-4", children: [watchedUrl?.match(/^https?:\/\//) &&
                                watch("module") &&
                                watch("name") &&
                                watch("remote") ? (_jsx(Button, { label: "Preview", icon: "pi pi-eye", text: true, className: "mr-auto px-4 py-2 border border-[#d1d5db] rounded-[6px] text-[#5928ED]", onClick: () => handlePreview({
                                    name: watch("name"),
                                    module: watch("module"),
                                    remote: watch("remote"),
                                    url: watch("url"),
                                }) })) : null, _jsx(Button, { type: "button", label: "Cancel Changes", icon: _jsx(X, { size: 16 }), className: "text-[#2546F0] px-4 py-2 rounded-[6px] flex items-center gap-2", outlined: true, onClick: () => {
                                    if (modeData) {
                                        reset({
                                            name: modeData.modeName,
                                            url: modeData.url,
                                            module: modeData.module?.replace("./", "") || "",
                                            remote: modeData.remote,
                                            modeDescription: modeData.modeDescription,
                                        });
                                        setFormEdited(false);
                                        handleCloseModeOnboarding();
                                    }
                                } }), _jsx(Button, { type: "submit", label: "Update Mode", icon: _jsx(Check, { size: 16 }), disabled: !formEdited, className: `${formEdited
                                    ? "bg-gradient-to-r from-[#2546F0] to-[#5928ED] text-white"
                                    : "bg-gray-300 text-gray-500"} px-4 py-2 rounded-[6px] flex items-center gap-2` })] })] })) : (_jsx(ModePreview, { formData: modePreviewData, setShowpreviewDialog: setShowpreviewDialog }))] }));
};
export default ModeOnboarding;
